// T-176 — Tray di caricamento multi-documento (sempre disponibile, async).
// Sostituisce il vecchio selettore a file singolo: l'utente aggiunge tanti
// file (foto/galleria/file PDF) in un vassoio, sceglie se sono documenti
// separati o le pagine di un unico documento, e li invia in un colpo solo.
// L'analisi AI gira in background (worker document_jobs); l'utente è libero di
// fare altro e ritrova i documenti nella sezione "In elaborazione".
//
// Riusa la stessa orchestrazione async della cattura singola (createDraft →
// runDraftUpload → coda document_jobs); non tocca la review/commit (che resta
// raggiungibile via "In elaborazione" → tap sul job pronto).

import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import {
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonSpinner,
  IonToast,
} from '@ionic/react';
import { getCaptureProvider } from '../lib/adapters/camera/index.js';
import { WebcamCaptureModal } from './WebcamCaptureModal.js';
import { prepareDocumentFile } from '../lib/documents/documentSource.js';
import { createDraft, runDraftUpload } from '../lib/documents/draftStore.js';
import { MAX_PAGES } from '../lib/documents/captureTray.js';
import { palette } from './design.js';
import { useHistory } from 'react-router-dom';
import { toUserMessage } from '../lib/errors.js';
import {
  captureTrayScopeKey,
  deleteCaptureTray,
  loadCaptureTray,
  saveCaptureTray,
  type CaptureTrayMode,
  type CaptureTrayScope,
} from '../lib/documents/captureTrayStore.js';

// Tetto documenti per invio (modalità "separati"). Non è un limite di prodotto
// sul ristoratore (vedi memoria feedback_no_app_side_limits): è solo la
// dimensione massima di UN batch interattivo, per non saturare RAM/upload in
// un colpo. Più batch successivi restano sempre possibili.
const MAX_DOCS_PER_BATCH = 25;
const MAX_RAW_BYTES = 50_000_000;

type UploadMode = CaptureTrayMode;

interface TrayItem {
  id: string;
  // draftId STABILE assegnato alla creazione: è la chiave di idempotenza
  // (client_draft_uuid) usata in invio. Riusarla su un retry evita di
  // ri-accodare lo stesso file come job duplicato dopo un errore a metà batch.
  draftId: string;
  b64: string; // senza prefisso data:
  mime: string;
  name: string;
  bytes: number;
  isPdf: boolean;
  thumb: string | null; // data URL per anteprima immagini (null per PDF/HEIC)
}

function bytesOfB64(b64: string): number {
  return Math.floor((b64.length * 3) / 4);
}

export function DocumentUploadTray(props: {
  accountId: string | null;
  tenantId: string | null;
  businessId: string | null;
  onNeedLocale: () => void;
  initialMode?: UploadMode;
  singleDocumentHint?: string;
}): JSX.Element {
  const { accountId, tenantId, businessId, onNeedLocale } = props;
  const history = useHistory();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // draftId stabile per la modalità "unico documento" (riusato su retry).
  const singleDraftIdRef = useRef<string | null>(null);
  const [mode, setMode] = useState<UploadMode>(props.initialMode ?? 'separate');
  const [items, setItems] = useState<TrayItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [webcamOpen, setWebcamOpen] = useState(false);
  const hydratedScopeRef = useRef<string | null>(null);
  const latestSnapshotRef = useRef<{ mode: UploadMode; items: TrayItem[]; singleDraftId: string | null }>({
    mode: props.initialMode ?? 'separate', items: [], singleDraftId: null,
  });
  const trayScope = useMemo<CaptureTrayScope | null>(() => (
    accountId && tenantId && businessId
      ? { accountId, tenantId, businessId }
      : null
  ), [accountId, businessId, tenantId]);
  const trayScopeKey = trayScope ? captureTrayScopeKey(trayScope) : null;

  useEffect(() => {
    hydratedScopeRef.current = null;
    setItems([]);
    setMode(props.initialMode ?? 'separate');
    singleDraftIdRef.current = null;
    if (!trayScope || !trayScopeKey) return;
    let active = true;
    void (async () => {
      const saved = await loadCaptureTray<TrayItem>(trayScope);
      if (!active) return;
      if (saved) {
        const restoredItems = saved.items.map((item) => ({
          ...item,
          thumb: item.isPdf || /heic|heif/i.test(item.mime)
            ? null
            : `data:${item.mime};base64,${item.b64}`,
        }));
        setItems(restoredItems);
        setMode(saved.mode);
        singleDraftIdRef.current = saved.singleDraftId;
        setToast(`Bozza recuperata: ${saved.items.length} file.`);
      }
      hydratedScopeRef.current = trayScopeKey;
    })();
    return () => { active = false; };
  }, [props.initialMode, trayScope, trayScopeKey]);

  latestSnapshotRef.current = {
    mode,
    items: items.map((item) => ({ ...item, thumb: null })),
    singleDraftId: singleDraftIdRef.current,
  };

  useEffect(() => {
    if (!trayScope || !trayScopeKey) return;
    return () => {
      if (hydratedScopeRef.current === trayScopeKey) {
        void saveCaptureTray(trayScope, latestSnapshotRef.current);
      }
    };
  }, [trayScope, trayScopeKey]);

  useEffect(() => {
    if (!trayScope || !trayScopeKey || hydratedScopeRef.current !== trayScopeKey) return;
    const timer = window.setTimeout(() => {
      void saveCaptureTray(trayScope, {
        mode,
        items: items.map((item) => ({ ...item, thumb: null })),
        singleDraftId: singleDraftIdRef.current,
      }).catch(() => setToast('Non riesco a salvare la bozza locale. Libera spazio e riprova.'));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [items, mode, trayScope, trayScopeKey]);
  const cap = mode === 'single' ? MAX_PAGES : MAX_DOCS_PER_BATCH;
  const remaining = Math.max(0, cap - items.length);

  function addItems(next: TrayItem[]): void {
    if (next.length === 0) return;
    setItems((prev) => {
      const room = Math.max(0, cap - prev.length);
      const accepted = next.slice(0, room);
      if (accepted.length < next.length) {
        setToast(
          mode === 'single'
            ? `Massimo ${MAX_PAGES} pagine per documento.`
            : `Massimo ${MAX_DOCS_PER_BATCH} documenti per invio. Invia questi e poi continua.`,
        );
      }
      return [...prev, ...accepted];
    });
  }

  // Conserva la risoluzione e la qualità del documento selezionato.
  async function imageToItem(rawB64: string, rawMime: string, name: string): Promise<TrayItem | null> {
    try {
      const c = await prepareDocumentFile(rawB64, rawMime);
      return {
        id: crypto.randomUUID(),
        draftId: crypto.randomUUID(),
        b64: c.b64,
        mime: c.mime,
        name,
        bytes: c.sizeBytes,
        isPdf: false,
        thumb: `data:${c.mime};base64,${c.b64}`,
      };
    } catch (error) {
      setToast(toUserMessage(error, `Impossibile leggere ${name}.`));
      return null;
    }
  }

  async function addFromCamera(): Promise<void> {
    if (remaining <= 0) { setToast('Vassoio pieno: invia questi documenti, poi continua.'); return; }
    setBusy(true);
    try {
      const photo = await getCaptureProvider().takePhoto('camera', { quality: 80 });
      if (!photo) return;
      const it = await imageToItem(photo.base64, photo.mime, `Foto ${new Date().toLocaleTimeString('it-IT')}`);
      if (it) addItems([it]);
    } catch (err) {
      setToast(toUserMessage(err, 'Scatto annullato'));
    } finally {
      setBusy(false);
    }
  }

  async function addFromWebcam(b64: string, mime: string): Promise<void> {
    if (remaining <= 0) { setToast('Vassoio pieno: invia questi documenti, poi continua.'); return; }
    setBusy(true);
    try {
      const it = await imageToItem(b64, mime, `Webcam ${new Date().toLocaleTimeString('it-IT')}`);
      if (it) addItems([it]);
    } catch (err) {
      setToast(toUserMessage(err, 'Errore acquisizione webcam'));
    } finally {
      setBusy(false);
    }
  }

  async function addFromGallery(): Promise<void> {
    if (remaining <= 0) { setToast('Vassoio pieno: invia questi documenti, poi continua.'); return; }
    setBusy(true);
    try {
      const photos = await getCaptureProvider().pickPhotos('gallery', remaining, { quality: 100 });
      const built: TrayItem[] = [];
      for (let i = 0; i < photos.length; i++) {
        const it = await imageToItem(photos[i].base64, photos[i].mime, `Immagine ${items.length + built.length + 1}`);
        if (it) built.push(it);
      }
      addItems(built);
    } catch (err) {
      setToast(toUserMessage(err, 'Selezione annullata'));
    } finally {
      setBusy(false);
    }
  }

  async function handleFiles(ev: ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = Array.from(ev.target.files ?? []);
    ev.target.value = '';
    if (files.length === 0) return;
    setBusy(true);
    try {
      const built: TrayItem[] = [];
      for (const file of files.slice(0, remaining)) {
        if (file.size > MAX_RAW_BYTES) {
          setToast(`"${file.name}" supera 50 MB. Seleziona un file più piccolo.`);
          continue;
        }
        const dataUrl: string = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result as string);
          r.onerror = () => reject(new Error('read_failed'));
          r.readAsDataURL(file);
        });
        const comma = dataUrl.indexOf(',');
        const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
        const mime = file.type || 'application/octet-stream';
        if (mime === 'application/pdf') {
          const bytes = bytesOfB64(b64);
          if (bytes > MAX_RAW_BYTES) {
            setToast(`"${file.name}" è troppo grande (${(bytes / 1_000_000).toFixed(1)} MB, max 50).`);
            continue;
          }
          built.push({ id: crypto.randomUUID(), draftId: crypto.randomUUID(), b64, mime, name: file.name, bytes, isPdf: true, thumb: null });
        } else {
          const it = await imageToItem(b64, mime.startsWith('image/') ? mime : 'image/jpeg', file.name);
          if (it) built.push(it);
        }
      }
      addItems(built);
    } catch (err) {
      setToast(toUserMessage(err, 'Errore lettura file'));
    } finally {
      setBusy(false);
    }
  }

  function removeItem(id: string): void {
    setItems((prev) => prev.filter((p) => p.id !== id));
  }

  async function send(): Promise<void> {
    if (items.length === 0) return;
    if (!tenantId) { setToast('Nessun cliente attivo. Cambialo dalle Impostazioni.'); return; }
    if (!businessId) { onNeedLocale(); return; }
    setBusy(true);
    const batch = [...items];
    try {
      if (mode === 'single') {
        // Un draftId STABILE per l'intero documento: se l'invio fallisce a metà
        // e l'utente ritenta, createDraft fa upsert e l'enqueue è idempotente su
        // client_draft_uuid → nessun job duplicato.
        if (!singleDraftIdRef.current) singleDraftIdRef.current = crypto.randomUUID();
        const draftId = singleDraftIdRef.current;
        if (trayScope) {
          await saveCaptureTray(trayScope, {
            mode,
            items: batch.map((item) => ({ ...item, thumb: null })),
            singleDraftId: draftId,
          });
        }
        await createDraft({
          draftId,
          tenantId,
          businessId,
          // Il tipo è un dettaglio del processo di estrazione: il worker lo
          // rileva dal documento e manda in revisione ogni caso ambiguo.
          docKind: null,
          pages: batch.map((it) => ({ b64: it.b64, mime: it.mime })),
        });
        await runDraftUpload(draftId);
        singleDraftIdRef.current = null;
        setItems([]);
        if (trayScope) await deleteCaptureTray(trayScope);
        setToast('Documento in analisi. Lo trovi in "In elaborazione".');
      } else {
        // Un documento (= un job) per file. Ogni item ha un draftId STABILE
        // (assegnato alla creazione) → un retry riusa la stessa chiave
        // idempotente, niente duplicati. Inoltre svuoto il vassoio item-per-item
        // man mano che ciascuno è accodato: un errore a metà lascia nel vassoio
        // solo i file non ancora inviati.
        for (const [index, it] of batch.entries()) {
          await createDraft({
            draftId: it.draftId,
            tenantId,
            businessId,
            docKind: null,
            pages: [{ b64: it.b64, mime: it.mime }],
          });
          await runDraftUpload(it.draftId);
          const remaining = batch.slice(index + 1);
          setItems(remaining);
          if (trayScope) {
            await saveCaptureTray(trayScope, {
              mode,
              items: remaining.map((item) => ({ ...item, thumb: null })),
              singleDraftId: null,
            });
          }
        }
        setToast(`${batch.length} ${batch.length === 1 ? 'documento' : 'documenti'} in analisi. Li trovi in "In elaborazione".`);
      }
      setTimeout(() => history.replace('/documents'), 700);
    } catch (err) {
      // I file già accodati sono stati tolti dal vassoio (modalità separati) o
      // riusano il draftId stabile (modalità unico): un retry non duplica.
      setToast(toUserMessage(err, 'Errore avvio analisi'));
    } finally {
      setBusy(false);
    }
  }

  const addButtons: Array<{ icon: string; label: string; sub: string; onClick: () => void }> = [
    { icon: '📸', label: 'Scatta una foto', sub: 'Attiva la luce; se non disponibile apre la fotocamera del telefono', onClick: () => setWebcamOpen(true) },
    { icon: '🖼️', label: 'Galleria', sub: 'Più foto dal telefono', onClick: () => void addFromGallery() },
    { icon: '📁', label: 'File', sub: 'PDF o immagini dal dispositivo', onClick: () => fileInputRef.current?.click() },
  ];

  const sendLabel = mode === 'single'
    ? `Invia documento${items.length > 0 ? ` (${items.length} pagine)` : ''}`
    : `Invia ${items.length || ''} ${items.length === 1 ? 'documento' : 'documenti'}`.replace('  ', ' ').trim();

  return (
    <div>
      {/* Modalità: documenti separati vs un unico documento multi-pagina.
          Label su 2 righe + wrap: i testi lunghi venivano troncati ("..."). */}
      <IonSegment
        value={mode}
        onIonChange={(e) => setMode((e.detail.value as UploadMode) ?? 'separate')}
        style={{ marginBottom: 6 }}
      >
        <IonSegmentButton value="separate" style={{ minHeight: 48 }}>
          <IonLabel style={{ whiteSpace: 'normal', fontSize: '0.76rem', lineHeight: 1.15, textTransform: 'none' }}>
            Documenti<br />separati
          </IonLabel>
        </IonSegmentButton>
        <IonSegmentButton value="single" style={{ minHeight: 48 }}>
          <IonLabel style={{ whiteSpace: 'normal', fontSize: '0.76rem', lineHeight: 1.15, textTransform: 'none' }}>
            Un unico<br />documento
          </IonLabel>
        </IonSegmentButton>
      </IonSegment>
      <div style={{ fontSize: 12, color: palette.textFaint, margin: '0 2px 14px', lineHeight: 1.4 }}>
        {mode === 'separate'
          ? 'Ogni file caricato diventa un documento a sé (es. più fatture insieme).'
          : props.singleDocumentHint ?? 'Tutti i file caricati sono le pagine di uno stesso documento (es. un DDT su più fogli).'}
      </div>

      {/* Bottoni di aggiunta (foto / galleria / file) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {addButtons.map((opt) => (
          <button
            key={opt.label}
            type="button"
            onClick={opt.onClick}
            disabled={busy}
            style={{
              padding: '16px', borderRadius: 14,
              border: `1px solid ${palette.border}`, background: palette.card,
              cursor: busy ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 16, textAlign: 'left',
              boxShadow: '0 1px 2px rgba(15,23,42,.04), 0 2px 6px rgba(15,23,42,.05)',
            }}
          >
            <span style={{ fontSize: 30, flexShrink: 0, lineHeight: 1 }}>{opt.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: palette.text }}>{opt.label}</div>
              <div style={{ fontSize: 12, color: palette.textFaint, marginTop: 2 }}>{opt.sub}</div>
            </div>
            <span style={{ color: palette.textFaint, fontSize: 22 }}>＋</span>
          </button>
        ))}
      </div>

      {/* Vassoio: anteprime dei file aggiunti */}
      {items.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: palette.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            {mode === 'single' ? `Pagine (${items.length}/${MAX_PAGES})` : `Documenti (${items.length})`}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {items.map((it, idx) => (
              <div
                key={it.id}
                style={{
                  position: 'relative', width: 92, height: 92, borderRadius: 12,
                  border: `1px solid ${palette.border}`, background: '#0f172a',
                  overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {it.isPdf || !it.thumb ? (
                  <div style={{ textAlign: 'center', color: '#cbd5e1', padding: 6 }}>
                    <div style={{ fontSize: 28 }}>{it.isPdf ? '📄' : '🖼️'}</div>
                    <div style={{ fontSize: 9, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 80 }}>
                      {it.name}
                    </div>
                  </div>
                ) : (
                  <img src={it.thumb} alt={it.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
                {mode === 'single' && (
                  <span style={{ position: 'absolute', left: 4, top: 4, background: 'rgba(15,23,42,.75)', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 6, padding: '1px 6px' }}>
                    {idx + 1}
                  </span>
                )}
                <button
                  type="button"
                  aria-label="Rimuovi"
                  onClick={() => removeItem(it.id)}
                  disabled={busy}
                  style={{
                    position: 'absolute', right: 3, top: 3, width: 22, height: 22, borderRadius: 11,
                    border: 'none', background: 'rgba(15,23,42,.8)', color: '#fff', fontSize: 14,
                    lineHeight: '22px', cursor: 'pointer', padding: 0,
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invio in un colpo solo */}
      <button
        type="button"
        onClick={() => void send()}
        disabled={busy || items.length === 0}
        style={{
          marginTop: 20, width: '100%', padding: '16px',
          borderRadius: 14, border: 'none',
          background: busy || items.length === 0 ? palette.border : palette.accent,
          color: '#fff', fontSize: 16, fontWeight: 800,
          cursor: busy || items.length === 0 ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        }}
      >
        {busy ? <IonSpinner name="dots" /> : null}
        {busy ? 'Invio in corso…' : `📤 ${sendLabel}`}
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,image/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => void handleFiles(e)}
      />

      <WebcamCaptureModal
        isOpen={webcamOpen}
        onClose={() => setWebcamOpen(false)}
        onFallback={() => void addFromCamera()}
        onCapture={(b64, mime) => void addFromWebcam(b64, mime)}
      />

      <IonToast
        isOpen={!!toast}
        message={toast ?? ''}
        duration={2600}
        onDidDismiss={() => setToast(null)}
      />
    </div>
  );
}
